import { useTheme } from '@mui/material/styles';
import { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  CssBaseline,
  Toolbar,
  List,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Grid,
  Tooltip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import OutlinedCard from '../components/OutlinedCard';
import { Navigate, Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addStore, addUsers, removeAdminAccess } from 'reduxStore/authSlice';
import logo from '../assets/images/auth/traLogo.png';
import { AppBar, Main, DrawerHeader } from './components/StyledComponents';
import { useSelector } from 'react-redux';
import AccountMenu from './components/AccountMenu';
import { style } from 'constant/constants';
import Text from '../components/Text';
import { getAuthRequest } from 'Api/api';
import CustomButton from 'components/Button';
import { roleBasedIcons, roleBasedDrawerWidth } from './components/NavigationPane';
import LogoutIcon from '@mui/icons-material/Logout';
import { isAdmin, isAreaAdmin, isSuperAdmin, isUser } from 'utils/utils';
import TextEllipsize from 'utils/TextEllipsize';
import StoreLogo from '../pages/storePages/StoreLog';
import { getStoreLogoPreviewUrl } from '../pages/storePages/storeUtils';

export let drawerWidth = 0;

const BasePage = () => {
  const { authToken, adminAccess, userData, storeData } = useSelector((store) => store.auth);
  const auth = !!authToken;
  const location = useLocation();

  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [icons, setIcons] = useState([]);
  const [openMenu, setOpenMenu] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [users, setUsers] = useState([]);
  const [logoUrl, setLogoUrl] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const getAllUsers = async () => {
    try {
      const usersList = await getAuthRequest('getUsersList');

      setUsers(usersList?.usersResponses);
    } catch (err) {
      return err;
    }
  };

  useEffect(() => {
    if (auth && userData) {
      let storeId = userData?.storeId;
      const stores = userData?.storesList;
      if (!storeId && Array.isArray(stores) && stores?.length > 0) {
        storeId = userData?.storesList[0]?.storeId;
      }
      if (storeId) {
        (async (storeId) => {
          try {
            const previewUrl = await getStoreLogoPreviewUrl(storeId);
            dispatch(addStore({ ...storeData, storeLogoURL: previewUrl }));
            setLogoUrl(previewUrl);
          } catch (err) {
            console.log(err);
          }
        })(storeId);
      }
    }
  }, [userData, auth]);

  useEffect(() => {
    if (auth) {
      setIcons(roleBasedIcons(userData?.roles));
      drawerWidth = roleBasedDrawerWidth(userData?.roles);
      getAllUsers();
    }
  }, [adminAccess]);

  // Function to map path to tab name
  const mapPathToTabName = (path) => {
    const matchingTab = roleBasedIcons(userData?.roles)?.find((item) => item.path === path);
    // Change 'DefaultTabName' to your default tab name if needed
    return matchingTab ? matchingTab.name : '';
  };

  // Enable tabs according to their path
  useEffect(() => {
    if (auth) {
      const tabName = mapPathToTabName(location.pathname);
      setSelectedItem(tabName);
    }
  }, [location]);

  useEffect(() => {
    if (auth) {
      dispatch(addUsers(users));
    }
  }, [users]);

  //handle selected item
  const handleItemSelect = (item) => {
    setSelectedItem(item);
  };

  const handleDrawerOpen = () => {
    setOpen(!open);
  };

  if (!auth) {
    return <Navigate to="/login" />;
  }

  if (location.pathname == '/' && (isAreaAdmin(userData?.roles) || isSuperAdmin(userData?.roles))) {
    return <Navigate to="/Stores" />;
  }
  if (location.pathname == '/' && (isAdmin(userData?.roles) || isUser(userData?.roles))) {
    return <Navigate to="/StoreInfo/projects" />;
  }

  if (location.pathname === '/StoreInfo' || location.pathname === '/StoreInfo/') {
    if (isAdmin(userData?.roles) || isUser(userData?.roles)) {
      return <Navigate to="/StoreInfo/projects" />;
    } else if (isSuperAdmin(userData?.roles) || isAreaAdmin(userData?.roles)) {
      return <Navigate to="/Error" />;
    }
  }

  const logoutFromStore = () => {
    dispatch(removeAdminAccess());

    navigate('/Stores', { replace: true });
    localStorage.setItem('logoutStoreHistory', true);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      {openMenu && <AccountMenu openMenu={openMenu} setOpenMenu={setOpenMenu} />}

      <AppBar position="fixed" open={open} style={{ backgroundColor: style.LIGHT.primary }}>
        <Toolbar
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            height: '1vh'
          }}>
          {isAreaAdmin(userData?.roles) ||
            (isSuperAdmin(userData?.roles) && (
              <Grid display="flex" sx={{ ml: '3vh' }}>
                <img src={logo} alt="logo" width={180} height={50} />
              </Grid>
            ))}

          <Grid display="flex" alignItems="center">
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{ mr: 2, ...(open && { display: 'none' }) }}>
              {(isAdmin(userData?.roles) || isUser(userData?.roles)) && (
                <Tooltip title="Expand">
                  <MenuIcon />
                </Tooltip>
              )}
            </IconButton>

            {adminAccess && (
              <Tooltip title="Logout from Store">
                <IconButton color="inherit" onClick={logoutFromStore}>
                  <LogoutIcon style={{ transform: 'rotate(180deg)', fontSize: '3vh' }} />
                  <Text noWrap component="div" variant="body1" style={{ paddingLeft: '0.5vh' }}>
                    Exit Store
                  </Text>
                </IconButton>
              </Tooltip>
            )}
          </Grid>

          <Grid display="flex">
            {isAdmin(userData?.roles) && (
              <StoreLogo logoUrl={logoUrl} maxWidth={100} sx={{ mr: 2 }} />
            )}
            <Text
              variant="h6"
              noWrap
              component="div"
              sx={{
                mr: '6vh',
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column'
              }}>
              {isUser(userData.roles) && (
                <TextEllipsize value={userData?.storesList[0]?.storeName} length={50} />
              )}
              {adminAccess && isAdmin(userData.roles) && (
                <TextEllipsize value={storeData?.storeName} length={50} />
              )}
              {adminAccess == false && isAdmin(userData.roles) && (
                <TextEllipsize value={userData?.storesList[0]?.storeName} length={50} />
              )}
            </Text>
          </Grid>
          <Grid display="flex">
            <CustomButton
              onClick={() => {
                setOpenMenu(!openMenu);
              }}>
              <Text variant="body1">{userData.fullName}</Text>
            </CustomButton>
          </Grid>
        </Toolbar>
      </AppBar>

      {(isAdmin(userData?.roles) || isUser(userData?.roles)) && (
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box'
            }
          }}
          variant="persistent"
          anchor="left"
          open={open}>
          <DrawerHeader>
            <div style={{ width: '100%' }}>
              <StoreLogo logoUrl={logoUrl} maxWidth={100} />
            </div>
            <IconButton onClick={handleDrawerOpen}>
              {theme.direction === 'ltr' && (
                <Tooltip title="Collapse">
                  <ChevronLeftIcon />
                </Tooltip>
              )}
            </IconButton>
          </DrawerHeader>
          <Divider />
          <List>
            {icons?.map(({ name, icon, path }) => (
              <ListItem component={Link} to={path} key={name} disablePadding>
                <ListItemButton
                  selected={selectedItem === name}
                  onClick={() => handleItemSelect(name)}>
                  <ListItemIcon>{icon}</ListItemIcon>
                  <ListItemText
                    primary={name}
                    className="text-menu-color-teal font-mono"
                    sx={{ font: style.fontFamily }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Drawer>
      )}
      <Main open={open} sx={{ backgroundColor: style.LIGHT.background.light }}>
        <DrawerHeader />
        <OutlinedCard>
          <Outlet />
        </OutlinedCard>
      </Main>
    </Box>
  );
};

export default BasePage;
